import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import classNames from 'classnames'

function Form({ clientSecret, formId, backPath }) {
  // region Initialization
  const stripe = useStripe()

  const [accountHolderName, setAccountHolderName] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // endregion

  // region Helper Functions
  function validateFields() {
    if (!accountHolderName) {
      setError("Account holder name can't be blank")
      return false
    }
    if (!email) {
      setError("Email can't be blank")
      return false
    }
    return true
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')

    if (!validateFields()) { return }

    setSubmitting(true)

    // Opens Stripe's hosted bank-account collection: Financial Connections for instant
    // verification, with manual entry + microdeposit verification as the fallback.
    const collectResult = await stripe.collectBankAccountForSetup({
      clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        payment_method_data: {
          billing_details: { name: accountHolderName, email }
        }
      },
      expand: ['payment_method']
    })

    if (collectResult.error) {
      setError(collectResult.error.message)
      setSubmitting(false)
      return
    }

    // The customer can close Stripe's dialog without picking/verifying a bank account — that
    // resolves without an error, but leaves the SetupIntent with no PaymentMethod attached
    // (status stays `requires_payment_method`). Confirming it anyway would fail server-side with
    // payment_intent_incompatible_payment_method, so treat this as a cancellation instead.
    if (collectResult.setupIntent?.status === 'requires_payment_method') {
      setError('Bank account setup was not completed. Please try again.')
      setSubmitting(false)
      return
    }

    // Confirm the SetupIntent — this presents and records the ACH debit mandate and attaches the
    // PaymentMethod to the customer. Status is `succeeded` for instant verification or
    // `requires_action` while microdeposits are pending (Stripe hosts that follow-up).
    const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret)

    if (confirmResult.error) {
      setError(confirmResult.error.message)
      setSubmitting(false)
      return
    }

    // PaymentMethod is attached client-side; let the server acknowledge and redirect. Pass the
    // SetupIntent status so the server can tell a verified account (succeeded) from one still
    // pending microdeposit verification (requires_action) and message accordingly.
    const statusField = document.querySelector(`#${formId} #setup_intent_status`)
    if (statusField) { statusField.value = confirmResult.setupIntent?.status || '' }
    document.querySelector(`#${formId}`).submit()
  }

  function renderInput(id, label, type, value, onChange) {
    return (
      <div className='form-group' data-testid={`add-payment-method-${id}`}>
        <label className='form-label' htmlFor={id}>
          <abbr title='required'>*</abbr> {label}
        </label>
        <input
          id={id}
          className='StripeElement form-control'
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    )
  }
  // endregion

  // region render
  if (!stripe) { return null }

  return (
    <form onSubmit={handleSubmit}>
      {renderInput('account-holder-name', 'Account Holder Name', 'text', accountHolderName, setAccountHolderName)}
      {renderInput('email', 'Email', 'email', email, setEmail)}

      {error && <div className={classNames('error_message', 'stripe_error')}>{error}</div>}

      <div className="text-right margin-top-md">
        <a href={backPath} className="btn-secondary">
          Cancel
        </a>
        <button className='btn-primary margin-left-sm' type="submit" disabled={!stripe || submitting}>
          Connect Bank Account
        </button>
      </div>
    </form>
  )
  // endregion
}

Form.propTypes = {
  clientSecret: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  backPath: PropTypes.string.isRequired
}

function PaymentMethodForm({ apiKey, clientSecret, formId, backPath }) {
  const stripePromise = useMemo(
    () => loadStripe(apiKey),
    [apiKey] // Ensure stripe is only loaded once
  )

  return (
    <Elements stripe={stripePromise}>
      <Form clientSecret={clientSecret} formId={formId} backPath={backPath} />
    </Elements>
  )
}

PaymentMethodForm.propTypes = {
  apiKey: PropTypes.string.isRequired,
  clientSecret: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  backPath: PropTypes.string.isRequired
}

export default PaymentMethodForm
